import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { Menu, LogIn } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Navigation } from "./Navigation";
import { AuthDialog } from "./AuthDialog";
import { useState, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { SearchBar } from "./navigation/SearchBar";
import { DesktopNavLinks } from "./navigation/DesktopNavLinks";
import { Link } from "react-router-dom";
import { NotificationDropdown } from "./notifications/NotificationDropdown";
import { Button } from "./ui/button";

export const TopNav = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isMobile } = useIsMobile();
  const [searchQuery, setSearchQuery] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  
  const { data: session, isLoading: sessionLoading } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      return session;
    },
  });
  
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['profile'],
    enabled: !!session?.user,
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        queryClient.invalidateQueries({ queryKey: ['session'] });
        queryClient.invalidateQueries({ queryKey: ['profile'] });
        queryClient.clear();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [queryClient]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setIsMenuOpen(false);
      setSearchQuery("");
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 border-b border-foreground/10 bg-background z-[100]">
      <div className="container flex h-16 md:h-20 max-w-screen-2xl items-center mx-auto px-4">
        {/* Left section - Logo and mobile menu */}
        <div className="flex items-center gap-4">
          <div className="lg:hidden">
            <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <SheetTrigger aria-label="Open navigation menu">
                <Menu className="h-6 w-6 text-foreground" />
              </SheetTrigger>
              <SheetContent side="left" className="w-[300px] bg-white p-0 overflow-y-auto">
                <Navigation
                  onSearch={handleSearch}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  onNavigate={() => setIsMenuOpen(false)}
                />
              </SheetContent>
            </Sheet>
          </div>

          
          <Link to="/" className="flex items-center" aria-label="Tomorrow33 home">
            <img 
              src="/lovable-uploads/2f6c15f0-0e51-4e82-83ca-b7895b7e3f3e.png" 
              alt="Tomorrow33 Disney fan community" 
              className="h-8 w-auto cursor-pointer"
            />
          </Link>
        </div>

        {/* Center section - Desktop Navigation */}
        <div className="hidden lg:flex flex-1 justify-center">
          <DesktopNavLinks isAuthenticated={!!session} />
        </div>

        {/* Right section - Search, Notifications and Profile */}
        <div className="flex items-center gap-2 sm:gap-4 ml-auto min-w-0">
          {!isMobile && (
            <div className="hidden lg:flex flex-1 max-w-xs">
              <SearchBar
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                handleSearch={handleSearch}
              />
            </div>
          )}


          
          <div className="flex items-center gap-2">
            {session && <NotificationDropdown />}
            {session && profile ? (
              <Avatar 
                role="button"
                tabIndex={0}
                aria-label="View your profile"
                className="cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => navigate(`/profile/${profile.id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/profile/${profile.id}`);
                  }
                }}
              >
                <AvatarImage src={profile.avatar_url} className="object-cover" alt={profile.username ? `${profile.username} profile photo` : 'Profile photo'} />
                <AvatarFallback className="bg-muted text-foreground">
                  {profile.username?.[0]?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
            ) : !sessionLoading && (
              <Button 
                variant="secondary"
                size="sm"
                className="flex items-center gap-2"
                onClick={() => setIsAuthOpen(true)}
              >
                <LogIn className="h-4 w-4" />
                Sign In
              </Button>
            )}
          </div>
        </div>
      </div>

      <AuthDialog 
        isOpen={isAuthOpen}
        onClose={() => setIsAuthOpen(false)}
        defaultTab="signin"
      />
    </header>
  );
};